import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { RealEstateImage } from '@immosky/types';
import { clamp, PerformanceTimer } from '@immosky/shared';
import { HammerGestureConfig } from '@angular/platform-browser';
import * as Hammer from 'hammerjs';
import { DIRECTION_HORIZONTAL } from 'hammerjs';

class ListCardHammerConfig extends HammerGestureConfig {
  buildHammer(element: HTMLElement): HammerManager {
    return new Hammer.Manager(element, {
      touchAction: 'pan-y',
      recognizers: [
        [
          Hammer.Pan,
          {
            direction: DIRECTION_HORIZONTAL,
            threshold: 10,
          },
        ],
      ],
    });
  }
}

@Component({
  selector: 'lib-list-card-slider',
  templateUrl: './list-card-slider.component.html',
  styleUrls: ['./list-card-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListCardSliderComponent
  implements AfterViewInit, AfterViewChecked
{
  timer!: PerformanceTimer;
  hammerInstance!: HammerManager;

  imageIndex = 0;

  loadRestOfImages = false;
  isPanning = false;

  panX = 0;
  sliderWidth = 0;
  currentX = 0;
  transform = 'translateX(0);';

  @Input()
  canSlide = true;

  @Input()
  carousel = true;

  @Input()
  images: RealEstateImage[] = [];

  @ViewChild('slider')
  sliderElement!: ElementRef<HTMLElement>;

  @Output()
  clicked = new EventEmitter();

  constructor(private cdRef: ChangeDetectorRef) {
    this.timer = new PerformanceTimer('list-card-slider');
  }

  ngAfterViewInit(): void {
    if (this.canSlide) {
      this.hammerInstance = new ListCardHammerConfig().buildHammer(
        this.sliderElement.nativeElement
      );
      this.hammerInstance.on('panstart', this.panStart.bind(this));
      this.hammerInstance.on('pan', this.pan.bind(this));
      this.hammerInstance.on('panend', this.panEnd.bind(this));
      this.hammerInstance.on('pancancel', this.panEnd.bind(this));

      // Load rest of images after first interaction
      this.sliderElement.nativeElement.addEventListener('mouseenter', this.loadImages, { once: true });
      this.sliderElement.nativeElement.addEventListener('touchstart', this.loadImages, { once: true });
    }
  }

  private loadImages = () => {
    this.loadRestOfImages = true;
    this.cdRef.markForCheck();
  }

  ngAfterViewChecked(): void {
    // this.timer.stop();
  }

  previousImage() {
    this.goToImage(this.imageIndex - 1);
  }

  nextImage() {
    this.goToImage(this.imageIndex + 1);
  }

  goToImage(index: number) {
    const max = this.images.length -1;
    const min = 0;
    let imageIndex;
    if (this.carousel === true) {
      imageIndex = index > max ? min : (index < min ? max : index);
    } else {
      imageIndex = clamp(min, index, max);
    }
    this.updateImageIndex(imageIndex);
  }

  panStart(event: HammerInput) {
    if (this.images.length < 2) {
      return;
    }

    this.updatePanX(0);

    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
      this.isPanning = true;
    }
  }

  pan(event: HammerInput) {
    if (this.images.length < 2) {
      return;
    }

    if (this.isPanning) {
      const panX =
        Math.min(Math.abs(event.deltaX), this.sliderWidth) *
        Math.sign(event.deltaX);
      this.updatePanX(panX);
    }
  }

  panEnd(event: HammerInput) {
    if (this.images.length < 2) {
      return;
    }

    const velocityHighEnough = Math.abs(event.velocityX) > 0.5;
    const pannedFarEnough = Math.abs(this.panX) > this.sliderWidth * 0.2;

    if (velocityHighEnough || pannedFarEnough) {
      const sign = Math.sign(this.panX);

      if (sign < 0) {
        this.nextImage();
      } else {
        this.previousImage();
      }
    }

    this.isPanning = false;
    this.updatePanX(0);
  }

  private updateImageIndex(index: number) {
    this.imageIndex = index;
    this.updateCurrentX();
  }

  private updatePanX(x: number) {
    this.panX = x;
    this.updateCurrentX();
  }

  private updateCurrentX() {
    this.updateSliderWidth();
    this.currentX = this.sliderWidth * this.imageIndex * -1 + this.panX;
    this.transform = `translateX(${this.currentX}px)`;
    this.cdRef.markForCheck();
  }

  private updateSliderWidth() {
    this.sliderWidth = this.sliderElement?.nativeElement?.clientWidth;
  }

  trackBy(index: number, image: RealEstateImage) {
    return image.url;
  }

  onLinkClick() {
    this.clicked.next();
  }
}
